'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import { toast } from '@/components/common/toast'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { Separator } from '@/components/ui/separator'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { __cAccountService } from '@/services/client/__c.account.service'
import { useMutation } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useState } from 'react'

interface VoucherCardProps {
  title: string
  subtitle: string
  code?: string
  buttonText: string
  bgColor: string
  textColor: string
  buttonVariant?: 'outline' | 'black' | 'primary'
  image?: string
  codeColor?: string
}

const VoucherCard = ({
  title,
  subtitle,
  code,
  buttonText,
  bgColor,
  textColor,
  buttonVariant = 'primary',
  image,
  codeColor,
}: VoucherCardProps) => {
  const t = useTranslations()
  const [isSaved, setIsSaved] = useState(false)
  const { mutate: saveMutation, isPending } = useMutation({
    mutationFn: (voucher: string) => __cAccountService.saveVoucher(voucher),
    onError: (error) => {
      toast.message({
        title: t('bra_legging_collection_hardcode.voucher.title'),
        message:
          error.message ||
          t('bra_legging_collection_hardcode.message.voucher_error'),
      })
    },
    onSuccess: (data) => {
      if (data.status === 'success') {
        toast.message({
          title: t('bra_legging_collection_hardcode.voucher.title'),
          message: data.message,
        })
        setIsSaved(true)
      }
    },
  })

  const handleSave = () => {
    if (!code) {
      //scroll to class
      const element = document.querySelector('#collection-listing')
      element?.scrollIntoView({ behavior: 'smooth' })
      return
    }

    if (isSaved) {
      // If already saved, scroll to products section
      const element = document.querySelector('#collection-listing')
      element?.scrollIntoView({ behavior: 'smooth' })
      return
    }

    saveMutation(code)
  }

  return (
    <div
      className={cn(
        'flex h-full max-w-full flex-col justify-end rounded-2xl px-6 py-3',
        bgColor
      )}
    >
      <div className='flex items-end justify-between'>
        <div>
          <div
            className={cn(
              'text-lg font-medium !leading-tight text-neutral-800 md:text-2xl 2xl:text-[32px]',
              textColor
            )}
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <p className={cn('text-xs text-neutral-800 md:text-sm', textColor)}>
            {subtitle}
          </p>
        </div>
        {image && (
          <Image
            src={ImageHelper.getImageUrlCdn(image)}
            alt='voucher'
            width={100}
            height={100}
            className='max-h-17 object-contain md:max-h-24'
          />
        )}
      </div>
      <Separator className='my-3' />
      <div className='flex items-center justify-between'>
        <p className={cn('text-sm md:text-base', textColor, codeColor)}>
          {code}
        </p>
        <Button
          variant={buttonVariant}
          className='w-fit justify-center whitespace-nowrap'
          size='small'
          onClick={handleSave}
          disabled={isPending}
        >
          {isPending
            ? t('bra_legging_collection_hardcode.voucher.save_pending')
            : isSaved
              ? t('bra_legging_collection_hardcode.voucher.buy_now')
              : buttonText}
        </Button>
      </div>
    </div>
  )
}

export default function VoucherCarousel() {
  const t = useTranslations()
  const vouchers = [
    {
      title: t('bra_legging_collection_hardcode.voucher.discount_10_percent'),
      subtitle: t('bra_legging_collection_hardcode.voucher.when_buy_2'),
      code: '',
      buttonText: t('bra_legging_collection_hardcode.voucher.buy_now'),
      bgColor: 'bg-neutral-100',
      textColor: 'text-neutral-900',
      buttonVariant: 'black' as const,
      image: '',
    },
    {
      title: t('bra_legging_collection_hardcode.voucher.free_accessory'),
      subtitle: t(
        'bra_legging_collection_hardcode.voucher.for_order_from_399k_optional'
      ),
      code: '',
      buttonText: t('bra_legging_collection_hardcode.voucher.buy_now'),
      bgColor: 'bg-neutral-100',
      textColor: 'text-neutral-900',
      buttonVariant: 'black' as const,
      image: '/image/July2025/mceclip14_21.png',
    },
  ]

  return (
    <CollectionContainer className='py-6 md:py-8'>
      <Carousel
        opts={{
          align: 'start',
          loop: false,
          slidesToScroll: 1,
        }}
        className='w-full'
      >
        <CarouselContent className='-ml-4 md:justify-center'>
          {vouchers.map((voucher, index) => (
            <CarouselItem
              key={index}
              className='basis-4/5 pl-4 md:basis-1/3 lg:basis-1/4'
            >
              <VoucherCard {...voucher} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselNavigation />
      </Carousel>
    </CollectionContainer>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-7 w-7 -translate-y-full lg:-left-10'
        variant='black'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-7 w-7 -translate-y-full lg:-right-10'
        variant='black'
        hideIfEnd
      />
    </>
  )
}
